import { useEffect, useState } from "react";
import { Trash2, MailOpen, FileDown } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import Pager from "@/components/site/Pager";
import { api, API_BASE, getToken } from "@/lib/api";
import { toast } from "sonner";

const PAGE_SIZE = 25;

export default function AdminFeedback() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  const load = () => api.get("/admin/feedback", { params: { page, page_size: PAGE_SIZE } })
    .then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page]);

  const toggleRead = async (id, val) => { await api.patch(`/admin/feedback/${id}/read?read=${val}`); load(); };
  const remove = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/feedback/${id}`); toast.success("Deleted"); load(); };

  const exportCsv = async () => {
    const token = getToken();
    const res = await fetch(`${API_BASE}/admin/exports/feedback`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) { toast.error("Export failed"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url; link.download = `feedback-${new Date().toISOString().slice(0,10)}.csv`; link.click();
    URL.revokeObjectURL(url);
    toast.success("Exported");
  };

  return (
    <div className="space-y-6" data-testid="admin-feedback">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-semibold text-slate-900">Feedback</h1>
        <Button variant="outline" onClick={exportCsv} data-testid="feedback-export-csv"><FileDown className="mr-1 h-4 w-4" />Export CSV</Button>
      </div>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <ul className="divide-y">
          {items.map((f) => (
            <li key={f.id} className="p-4 hover:bg-slate-50">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-900">{f.name}</span>
                    <Badge variant="secondary" className="bg-slate-100">{f.category}</Badge>
                    <span className="text-xs text-secondary">{"★".repeat(f.rating || 5)}</span>
                    {!f.is_read && <Badge className="bg-blue-100 text-blue-700">New</Badge>}
                  </div>
                  <div className="text-xs text-slate-500">{f.email} · {new Date(f.created_at).toLocaleString()}</div>
                  <p className="mt-2 whitespace-pre-line text-sm text-slate-700">{f.message}</p>
                </div>
                <div className="flex shrink-0 gap-1">
                  <Button variant="ghost" size="sm" onClick={() => toggleRead(f.id, !f.is_read)} data-testid={`feedback-read-${f.id}`}><MailOpen className="h-3.5 w-3.5" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(f.id)} data-testid={`feedback-delete-${f.id}`}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
                </div>
              </div>
            </li>
          ))}
          {items.length === 0 && <li className="p-10 text-center text-slate-400">No feedback yet.</li>}
        </ul>
      </div>
      <Pager page={page} pageSize={PAGE_SIZE} total={total} onChange={setPage} testIdPrefix="admin-feedback-pager" />
    </div>
  );
}
